import React, { useState, useEffect } from 'react';
import {
    Activity,
    AlertTriangle,
    CheckCircle,
    Cpu,
    Database,
    Eye,
    EyeOff,
    TrendingUp,
    Zap,
    Shield,
    AlertCircle,
    Info,
    Settings,
    Wrench,
    DollarSign,
    Clock,
    BookOpen,
    Target,
    Brain
} from 'lucide-react';
import { Inspection } from '../../types/inspection';
import { aiAnalysisService } from '../../services/aiAnalysisService';
import { format } from "date-fns";
import { fr, enUS, ar } from "date-fns/locale";
import { useLanguage } from '../../hooks/useLanguage';

interface AIAnalysisProps {
    inspection: Inspection;
    onAnalysisComplete?: (analysisData: any) => void;
}

export const AIAnalysis: React.FC<AIAnalysisProps> = React.memo(({ inspection, onAnalysisComplete }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const [analysis, setAnalysis] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [showTechnicalData, setShowTechnicalData] = useState<boolean>(false);
    const [showOBDDetails, setShowOBDDetails] = useState<boolean>(false);
    const [obdDetails, setObdDetails] = useState<any>(null);

    // Get date locale
    const getDateLocale = () => {
        switch (currentLanguage) {
            case 'fr': return fr;
            case 'ar': return ar;
            default: return enUS;
        }
    };

    // Function to adapt labels according to context
    const getContextualLabels = () => {
        const hasOBDData = inspection.obdData?.errorCodes && inspection.obdData.errorCodes.length > 0;

        return {
            analysisTitle: hasOBDData ? t('aiAnalysis.labels.enrichedAnalysis') : t('aiAnalysis.labels.intelligentAnalysis'),
            analysisDescription: hasOBDData
                ? t('aiAnalysis.labels.enrichedDescription')
                : t('aiAnalysis.labels.intelligentDescription'),
            correlationTitle: hasOBDData ? t('aiAnalysis.labels.enrichedCorrelation') : t('aiAnalysis.labels.completeAnalysis'),
            dataSourceLabel: hasOBDData ? t('aiAnalysis.labels.technicalDataIntegrated') : t('aiAnalysis.labels.visualAnalysis'),
            dataSourceIcon: hasOBDData ? Database : Brain,
            confidenceLabel: hasOBDData ? t('aiAnalysis.labels.technicalReliability') : t('aiAnalysis.labels.diagnosticConfidence'),
            codeLabel: hasOBDData ? t('aiAnalysis.labels.technicalCodes') : t('aiAnalysis.labels.analyzedElements')
        };
    };

    // ⭐ Fonction pour charger l'analyse existante depuis l'inspection
    const loadExistingAnalysis = async () => {
        try {
            setLoading(true);
            setError(null);

            // ⭐ Vérifier si aiAnalysis existe (singulier maintenant)
            if (inspection.aiAnalysis) {
                // Si c'est déjà un objet complet
                if (typeof inspection.aiAnalysis === 'object' && 'summary' in inspection.aiAnalysis) {
                    setAnalysis(inspection.aiAnalysis);
                    if (onAnalysisComplete) onAnalysisComplete(inspection.aiAnalysis);
                } else {
                    // Si c'est juste un ID, charger l'analyse
                    const analysisData = await aiAnalysisService.getAnalyses(inspection._id);
                    if (analysisData) {
                        setAnalysis(analysisData);
                        if (onAnalysisComplete) onAnalysisComplete(analysisData);
                    }
                }
            } else {
                // Pas d'analyse disponible
                setError(t('aiAnalysis.errors.noAnalysisAvailable'));
            }
        } catch (err: any) {
            console.error('Error loading AI analysis:', err);
            setError(t('aiAnalysis.errors.loadingError'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadExistingAnalysis();
    }, [inspection._id, inspection.aiAnalysis]);

    // Function to get color according to severity
    const getSeverityColor = (severity: string) => {
        if (!severity) {
            return 'bg-gray-100 text-gray-800 border-gray-300';
        }

        const severityKey = severity.toLowerCase();
        const severityMap = {
            'critique': 'bg-red-100 text-red-800 border-red-300',
            'critical': 'bg-red-100 text-red-800 border-red-300',
            'high': 'bg-orange-100 text-orange-800 border-orange-300',
            'moderate': 'bg-yellow-100 text-yellow-800 border-yellow-300',
            'faible': 'bg-blue-100 text-blue-800 border-blue-300',
            'low': 'bg-blue-100 text-blue-800 border-blue-300'
        };

        return severityMap[severityKey as keyof typeof severityMap] || 'bg-gray-100 text-gray-800 border-gray-300';
    };

    // Function to get icon according to severity
    const getSeverityIcon = (severity: string) => {
        if (!severity) {
            return <Shield className="w-4 h-4" />;
        }

        const severityKey = severity.toLowerCase();
        const iconMap = {
            'critique': <AlertTriangle className="w-4 h-4" />,
            'critical': <AlertTriangle className="w-4 h-4" />,
            'élevée': <AlertCircle className="w-4 h-4" />,
            'high': <AlertCircle className="w-4 h-4" />,
            'modérée': <Info className="w-4 h-4" />,
            'moderate': <Info className="w-4 h-4" />,
            'faible': <CheckCircle className="w-4 h-4" />,
            'low': <CheckCircle className="w-4 h-4" />
        };

        return iconMap[severityKey as keyof typeof iconMap] || <Shield className="w-4 h-4" />;
    };

    const getObdStatusColor = (param: string, value: number): string => {
        switch (param) {
            case 'batteryVoltage':
                if (value < 11.5) return 'text-red-600 bg-red-50 border-red-200';
                if (value < 12.3) return 'text-orange-600 bg-orange-50 border-orange-200';
                return 'text-green-600 bg-green-50 border-green-200';
            case 'coolantTemp':
                if (value > 105) return 'text-red-600 bg-red-50 border-red-200';
                if (value > 95) return 'text-orange-600 bg-orange-50 border-orange-200';
                return 'text-green-600 bg-green-50 border-green-200';
            case 'fuelLevel':
                if (value < 10) return 'text-red-600 bg-red-50 border-red-200';
                if (value < 25) return 'text-orange-600 bg-orange-50 border-orange-200';
                return 'text-green-600 bg-green-50 border-green-200';
            default: return 'text-blue-600 bg-blue-50 border-blue-200';
        }
    };

    const getDiagnosticIcon = (type: string) => {
        switch (type) {
            case 'OBD_CONFIRMED': return <Zap className="w-5 h-5 text-green-600" />;
            case 'OBD_ENHANCED': return <Activity className="w-5 h-5 text-blue-600" />;
            case 'VISUAL_ONLY': return <Brain className="w-5 h-5 text-indigo-600" />;
            default: return <Cpu className="w-5 h-5 text-indigo-600" />;
        }
    };

    const getDiagnosticLabel = (type: string) => {
        const hasOBDData = inspection.obdData?.errorCodes && inspection.obdData.errorCodes.length > 0;

        switch (type) {
            case 'OBD_CONFIRMED': return t('aiAnalysis.diagnosticTypes.confirmedTechnical');
            case 'OBD_ENHANCED': return t('aiAnalysis.diagnosticTypes.enrichedTechnical');
            case 'VISUAL_ONLY': return hasOBDData ? t('aiAnalysis.diagnosticTypes.technicalAnalysis') : t('aiAnalysis.diagnosticTypes.intelligentAnalysis');
            default: return t('aiAnalysis.diagnosticTypes.intelligentAnalysis');
        }
    };

    const getObdParamName = (param: string): string => {
        return t(`aiAnalysis.parameters.${param}`, param);
    };

    const getObdUnit = (param: string): string => {
        const unitMap: { [key: string]: string } = {
            'batteryVoltage': t('aiAnalysis.units.voltage'),
            'coolantTemp': t('aiAnalysis.units.temperature'),
            'fuelLevel': t('aiAnalysis.units.percentage'),
            'rpm': t('aiAnalysis.units.rpm'),
            'engineLoad': t('aiAnalysis.units.percentage'),
            'throttlePosition': t('aiAnalysis.units.percentage')
        };
        return unitMap[param] || '';
    };

    // ⭐ Pendant le chargement
    if (loading) {
        return (
            <div className={`bg-indigo-50 p-6 rounded-lg border ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className={`flex items-center justify-center space-x-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                    <Cpu className="w-6 h-6 text-indigo-600 animate-pulse" />
                    <p className="text-indigo-600 font-medium">{t('aiAnalysis.sections.generateAnalysis.analyzing')}</p>
                </div>
            </div>
        );
    }

    // ⭐ En cas d'erreur (pas d'analyse disponible)
    if (error || !analysis) {
        return (
            <div className={`bg-gray-50 p-6 rounded-lg border ${isRTL ? 'rtl' : 'ltr'}`}>
                <div className={`flex items-center space-x-2 mb-3 ${isRTL ? 'space-x-reverse' : ''}`}>
                    <Info className="w-5 h-5 text-gray-600" />
                    <p className="text-gray-600 font-medium">
                        {t('aiAnalysis.errors.loadingError')}
                    </p>
                </div>
                <p className="text-sm text-gray-500">
                    {t('aiAnalysis.errors.technicalDetailsError')}
                </p>
            </div>
        );
    }

    // ⭐ Si l'analyse existe, afficher les détails
    const labels = getContextualLabels();
    const DataIcon = labels.dataSourceIcon;
    const hasOBDData = inspection.obdData?.errorCodes && inspection.obdData.errorCodes.length > 0;

    return (
        <div className={`bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-lg space-y-4 border ${isRTL ? 'rtl' : 'ltr'}`}>
            {/* Header */}
            <div className="flex items-center justify-between" dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="flex items-center gap-3">
                    {getDiagnosticIcon(analysis.diagnosticType)}
                    <div>
                        <h3 className="font-semibold text-gray-800">{getDiagnosticLabel(analysis.diagnosticType)}</h3>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1">
                                <DataIcon className="w-3 h-3" />
                                {labels.dataSourceLabel}
                            </span>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium hover:underline transition-colors"
                >
                    {expanded ? t('aiAnalysis.actions.collapse') : t('aiAnalysis.actions.expand')}
                </button>
            </div>

            {/* Summary section */}
            <div className="bg-white p-4 rounded-md shadow-sm border">
                <div dir={isRTL ? 'rtl' : 'ltr'}>
                    <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <Target className="w-4 h-4 text-indigo-600" />
                        {t('aiAnalysis.sections.summary.title')}
                    </h4>
                    <p className={`text-gray-700 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                        {analysis.summary}
                    </p>
                </div>

                <div className={`mt-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                        {analysis.damageDetected ? (
                            <div className={`flex items-center text-amber-600 bg-amber-50 px-3 py-1 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <AlertTriangle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                <span className="text-sm font-medium">{t('aiAnalysis.sections.summary.problemsDetected')}</span>
                            </div>
                        ) : (
                            <div className={`flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <CheckCircle className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                <span className="text-sm font-medium">{t('aiAnalysis.sections.summary.noProblemsApparent')}</span>
                            </div>
                        )}
                    </div>

                    {analysis.obdCorrelation?.confidence && (
                        <div className={`flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded-full ${isRTL ? 'space-x-reverse' : ''}`}>
                            <TrendingUp className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-blue-700 font-semibold">
                                {labels.confidenceLabel}: {analysis.obdCorrelation.confidence}%
                            </span>
                        </div>
                    )}
                </div>

                {/*{hasOBDData && analysis.obdCorrelation?.severityAnalysis?.recommendedAction && (
                    <div className={`mt-3 p-3 rounded-md border-l-4 ${
                        analysis.obdCorrelation.severityAnalysis.maxSeverity === 'critique'
                            ? 'bg-red-50 border-red-400 text-red-800'
                            : analysis.obdCorrelation.severityAnalysis.maxSeverity === 'élevée'
                                ? 'bg-orange-50 border-orange-400 text-orange-800'
                                : 'bg-yellow-50 border-yellow-400 text-yellow-800'
                    }`}>
                        <div className={`flex items-center space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                            <Clock className="w-4 h-4" />
                            <span className="font-medium text-sm">{t('aiAnalysis.sections.summary.recommendedAction')}</span>
                        </div>
                        <p className="text-sm mt-1">{analysis.obdCorrelation.severityAnalysis.recommendedAction}</p>
                    </div>
                )}*/}
            </div>

            {/* Extended content */}
            {expanded && (
                <div className="space-y-4">
                    {/* Vehicle data section */}
                    {hasOBDData && (
                        <div
                            className={`bg-white p-4 rounded-md ${
                                isRTL ? 'border-r-4' : 'border-l-4'
                            } border-blue-500`}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <Database className="w-4 h-4 text-blue-600" />
                                {t('aiAnalysis.sections.vehicleData.title')}
                            </h4>

                            <div className="mb-4">
                                <p className={`text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {t('aiAnalysis.sections.vehicleData.errorCodes')}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {inspection.obdData?.errorCodes.map((code: string, index: number) => (
                                        <span
                                            key={index}
                                            className="bg-red-100 text-red-800 text-xs px-3 py-1 rounded-full font-mono font-medium border border-red-200"
                                        >
                                            {code}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {(() => {
                                const filteredEntries = inspection.obdData?.liveData
                                    ? Object.entries(inspection.obdData.liveData)
                                        .filter(([_, value]) => value !== undefined && value !== null && value > 0)
                                    : [];

                                return filteredEntries.length > 0 && (
                                    <div>
                                        <p className={`text-sm font-medium text-gray-700 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
                                            {t('aiAnalysis.sections.vehicleData.realTimeParams')}
                                        </p>
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                            {filteredEntries.map(([param, value]) => (
                                                <div
                                                    key={param}
                                                    className={`p-3 rounded-lg text-center border-2 ${getObdStatusColor(param, value as number)}`}
                                                >
                                                    <div className="text-xs font-medium opacity-75 mb-1">
                                                        {getObdParamName(param)}
                                                    </div>
                                                    <div className="font-bold text-lg">
                                                        {value}{getObdUnit(param)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className={`text-xs text-blue-600 mt-4 bg-blue-50 p-2 rounded ${isRTL ? 'text-right' : 'text-left'}`}>
                                {t('aiAnalysis.sections.vehicleData.dataFrom')} {format(
                                new Date(inspection.obdData?.timestamp || new Date()),
                                'PPP à HH:mm',
                                { locale: getDateLocale() }
                            )}
                            </div>
                        </div>
                    )}

                    {/* Correlation */}
                    {analysis.obdCorrelation && (hasOBDData || analysis.obdCorrelation.correlation) && (
                        <div
                            className={`bg-white p-4 rounded-md ${
                                isRTL ? 'border-r-4' : 'border-l-4'
                            } border-green-500`}
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <Zap className="w-4 h-4 text-green-600" />
                                {labels.correlationTitle}
                            </h4>

                            {analysis.obdCorrelation.correlation && (
                                <div className="bg-green-50 p-4 rounded-lg mb-4">
                                    <p className={`text-gray-700 text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                                        {analysis.obdCorrelation.correlation}
                                    </p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {analysis.obdCorrelation.confidence && (
                                    <div className="bg-green-50 p-3 rounded-lg">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-medium text-green-700">{labels.confidenceLabel}:</span>
                                            <span className="text-sm font-bold text-green-800">
                                                {analysis.obdCorrelation.confidence}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-green-200 rounded-full h-2" dir="ltr">
                                            <div
                                                className="bg-green-600 h-2 rounded-full transition-all duration-500"
                                                style={{ width: `${analysis.obdCorrelation.confidence}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Detected issues */}
                    {analysis.detectedIssues && analysis.detectedIssues.length > 0 && (
                        <div className="bg-white p-4 rounded-md border border-amber-200" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <AlertTriangle className="w-4 h-4 text-amber-600" />
                                {t('aiAnalysis.sections.detectedIssues.title')}
                            </h4>
                            <ul className="space-y-2">
                                {analysis.detectedIssues.map((issue: string, index: number) => (
                                    <li key={index} className={`flex items-start gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <span className="text-amber-500 mt-1">•</span>
                                        <span className="text-gray-700 text-sm">{issue}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Recommended repairs */}
                    {analysis.estimatedRepairs && (
                        <div className="bg-white p-4 rounded-md border border-blue-200" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <Wrench className="w-4 h-4 text-blue-600" />
                                {t('aiAnalysis.sections.recommendedRepairs.title')}
                            </h4>
                            <div className="bg-blue-50 p-3 rounded-lg">
                                <p className={`text-gray-700 text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                                    {analysis.estimatedRepairs}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Cost estimation */}
                    {analysis.estimatedCostRange && (
                        <div className="bg-white p-4 rounded-md border border-green-200" dir={isRTL ? 'rtl' : 'ltr'}>
                            <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                                <DollarSign className="w-4 h-4 text-green-600" />
                                {t('aiAnalysis.sections.costEstimate.title')}
                            </h4>
                            <div className="flex items-center justify-between bg-green-50 p-4 rounded-lg">
                                <div className={isRTL ? 'text-right' : 'text-left'}>
                                    <p className="text-lg font-bold text-green-700">{analysis.estimatedCostRange}</p>
                                    <p className="text-xs text-gray-600 mt-1">
                                        *{hasOBDData ? t('aiAnalysis.sections.costEstimate.preciseEstimate') : t('aiAnalysis.sections.costEstimate.approximateEstimate')} {t('aiAnalysis.sections.costEstimate.fullDiagnostic')}
                                    </p>
                                </div>
                                {hasOBDData && (
                                    <div>
                                        <span className="text-xs text-green-600 bg-green-100 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                                            <Database className="w-3 h-3" />
                                            {t('aiAnalysis.sections.costEstimate.preciseEstimate')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Abnormal technical parameters */}
                    {analysis.technicalData?.abnormalParams && analysis.technicalData.abnormalParams.length > 0 && (
                        <div className="bg-white p-4 rounded-md border border-orange-200" dir={isRTL ? 'rtl' : 'ltr'}>
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-semibold flex items-center gap-2">
                                    <Activity className="w-4 h-4 text-orange-600" />
                                    {t('aiAnalysis.sections.abnormalParams.title')}
                                </h4>
                                <button
                                    onClick={() => setShowTechnicalData(!showTechnicalData)}
                                    className="text-xs text-orange-600 hover:underline flex items-center gap-1"
                                >
                                    {showTechnicalData ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                    {showTechnicalData ? t('aiAnalysis.sections.technicalDetails.hideDetails') : t('aiAnalysis.sections.technicalDetails.showDetails')}
                                </button>
                            </div>

                            {showTechnicalData && (
                                <div className="space-y-3">
                                    {analysis.technicalData.abnormalParams.map((param: any, index: number) => {
                                        const severityColors = {
                                            'HIGH': 'bg-red-50 border-red-300 text-red-800',
                                            'MEDIUM': 'bg-orange-50 border-orange-300 text-orange-800',
                                            'LOW': 'bg-yellow-50 border-yellow-300 text-yellow-800'
                                        };

                                        return (
                                            <div
                                                key={index}
                                                className={`p-4 rounded-lg ${severityColors[param.severity as keyof typeof severityColors]} ${
                                                    isRTL ? 'border-r-4' : 'border-l-4'
                                                }`}
                                            >
                                                <div className="flex justify-between items-start">
                                                    <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                                                        <p className="font-semibold text-sm">{getObdParamName(param.param)}</p>
                                                        <p className="text-xs opacity-75 mt-1">
                                                            {t('aiAnalysis.sections.abnormalParams.expectedValue')}: {param.expected}
                                                        </p>
                                                    </div>
                                                    <div className={`${isRTL ? 'text-left' : 'text-right'} ${isRTL ? 'mr-4' : 'ml-4'}`}>
                                                        <p className="font-bold text-lg">{param.value}{getObdUnit(param.param)}</p>
                                                        <span className="text-xs px-2 py-1 rounded-full bg-white bg-opacity-70 font-medium inline-block mt-1">
                                                            {param.severity}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Analysis metadata */}
                    <div className="bg-gray-50 p-4 rounded-md border" dir={isRTL ? 'rtl' : 'ltr'}>
                        <h4 className={`font-semibold mb-3 flex items-center gap-2 text-gray-700 ${isRTL ? 'text-right' : 'text-left'}`}>
                            <Info className="w-4 h-4" />
                            {t('aiAnalysis.sections.analysisInfo.title')}
                        </h4>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className={isRTL ? 'text-right' : 'text-left'}>
                                <p className="text-gray-600">
                                    <span className="font-medium">{t('aiAnalysis.sections.analysisInfo.diagnosticType')}</span> {getDiagnosticLabel(analysis.diagnosticType)}
                                </p>
                                <p className="text-gray-600 mt-1">
                                    <span className="font-medium">{t('aiAnalysis.sections.analysisInfo.dataSource')}</span> {hasOBDData ? t('aiAnalysis.sections.analysisInfo.technicalVisual') : t('aiAnalysis.sections.analysisInfo.visual')}
                                </p>
                                {analysis.obdCorrelation?.confidence && (
                                    <p className="text-gray-600 mt-1">
                                        <span className="font-medium">{t('aiAnalysis.sections.analysisInfo.confidenceLevel')}</span> {analysis.obdCorrelation.confidence}%
                                    </p>
                                )}
                            </div>

                            <div className={isRTL ? 'text-right' : 'text-left'}>
                                <p className="text-gray-600">
                                    <span className="font-medium">{t('aiAnalysis.sections.analysisInfo.generatedOn')}</span> {format(new Date(analysis.generatedAt), 'PPP à HH:mm', { locale: getDateLocale() })}
                                </p>
                                {analysis.isEdited && analysis.lastEditedAt && (
                                    <p className="text-gray-600 mt-1">
                                        <span className="font-medium">{t('aiAnalysis.sections.analysisInfo.modifiedOn')}</span> {format(new Date(analysis.lastEditedAt), 'PPP à HH:mm', { locale: getDateLocale() })}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});